import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { PatientDetails, SessionInfo } from '../../lib/types';
import { formatDateTime } from '../../lib/format';
import { hasRole, useMe } from '../../lib/session';
import { Alert, ErrorMessage, Field, Loading, PageTitle } from '../../components/ui';
import { PatientContactForm } from '../../components/PatientContactForm';
import { PrivacySection } from './PrivacySection';
import { SecuritySection } from './SecuritySection';
import { Pii } from '../../components/Pii';

export function AccountPage() {
  const { data: me } = useMe();
  if (!me) return <Loading />;
  return (
    <>
      <PageTitle>Moje konto</PageTitle>
      {me.mfaSetupRequired && (
        <Alert kind="warning">
          Konta administratorów i recepcji muszą mieć drugi składnik logowania. Włącz aplikację uwierzytelniającą lub dodaj klucz dostępu, aby
          korzystać z systemu.
        </Alert>
      )}
      <div className="grid-2">
        <EmailSection email={me.email} />
        <SessionsSection />
      </div>
      {hasRole(me, 'Patient') && <MyContactDetails patientId={me.id} />}
      <SecuritySection />
      {hasRole(me, 'Patient') && <PrivacySection />}
    </>
  );
}

function MyContactDetails({ patientId }: { patientId: string }) {
  const patient = useQuery({ queryKey: ['patient', patientId], queryFn: () => api.get<PatientDetails>(`/api/v1/patients/${patientId}`) });
  if (patient.error) return <ErrorMessage error={patient.error} />;
  if (!patient.data) return <Loading />;
  return <PatientContactForm key={patient.data.id} patient={patient.data} title="Dane kontaktowe" />;
}

function EmailSection({ email }: { email: string }) {
  const [value, setValue] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<unknown>(null);
  return (
    <section className="card" aria-labelledby="email-title">
      <h2 id="email-title">Adres e-mail</h2>
      <p>
        <Pii>{email}</Pii>
      </p>
      <ErrorMessage error={error} />
      {sent && <Alert kind="success">Wysłaliśmy link potwierdzający na nowy adres.</Alert>}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          api.post('/api/v1/account/email', { newEmail: value }).then(() => setSent(true), setError);
        }}
      >
        <Field label="Nowy adres e-mail" name="newEmail" type="email" autoComplete="email" value={value} onChange={setValue} required />
        <button type="submit" className="secondary">
          Zmień adres
        </button>
      </form>
    </section>
  );
}

function SessionsSection() {
  const client = useQueryClient();
  const sessions = useQuery({ queryKey: ['sessions'], queryFn: () => api.get<SessionInfo[]>('/api/v1/account/sessions') });
  const refresh = () => client.invalidateQueries({ queryKey: ['sessions'] });
  if (!sessions.data) return <Loading />;
  return (
    <section className="card" aria-labelledby="sessions-title">
      <h2 id="sessions-title">Aktywne sesje</h2>
      <ul className="plain-list">
        {sessions.data.map((s) => (
          <li key={s.id}>
            <span>{describeAgent(s.userAgent)}</span> <span className="muted">· ostatnio {formatDateTime(s.lastSeenAt)}</span>
            {s.isCurrent ? (
              <strong> (ta sesja)</strong>
            ) : (
              <button type="button" className="link-button danger" onClick={() => void api.delete(`/api/v1/account/sessions/${s.id}`).then(refresh)}>
                Wyloguj
              </button>
            )}
          </li>
        ))}
      </ul>
      <button type="button" className="secondary" onClick={() => void api.post('/api/v1/account/sessions/revoke-others').then(refresh)}>
        Wyloguj wszystkie inne sesje
      </button>
    </section>
  );
}

function describeAgent(agent: string | null): string {
  if (!agent) return 'Nieznane urządzenie';
  const browser = /Firefox/.test(agent) ? 'Firefox' : /Edg\//.test(agent) ? 'Edge' : /Chrome/.test(agent) ? 'Chrome' : /Safari/.test(agent) ? 'Safari' : 'Przeglądarka';
  const os = /Android/.test(agent) ? 'Android' : /iPhone|iPad/.test(agent) ? 'iOS' : /Windows/.test(agent) ? 'Windows' : /Mac OS/.test(agent) ? 'macOS' : /Linux/.test(agent) ? 'Linux' : '';
  return os ? `${browser}, ${os}` : browser;
}
